import { Progress as BaseProgress, type ProgressRootProps } from '@base-ui/react';
import { match } from 'ts-pattern';

import { useIntl } from '~/hooks/useIntl';
import { clsx, clsxs } from '~/styles/utils';

import { indicatorStyle, labelStyle, rootStyle, trackStyle, valueStyle, wrapperDirectionStyles, wrapperStyle } from './Progress.css';

const currencyFormat: Intl.NumberFormatOptions = { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 };
const percentFormat: Intl.NumberFormatOptions = { style: 'percent' };

type Props = ProgressRootProps & {
  label?: string;
  type: 'currency' | 'percent';
  /** A `null` value renders the indeterminate state, for progress of an unknown amount. */
  value: number | null;
};

export const Progress = ({ label, type, ...delegated }: Props) => {
  const { locale } = useIntl();

  const defaultFormat = match(type)
    .with('currency', () => currencyFormat)
    .with('percent', () => percentFormat)
    .exhaustive();
  const format = delegated.format ?? defaultFormat;

  const defaultMax = type === 'percent' ? 1 : undefined;
  const max = delegated.max ?? defaultMax;
  const formattedMax = delegated.max !== undefined ? new Intl.NumberFormat(locale, format).format(delegated.max) : null;

  return (
    <BaseProgress.Root
      {...delegated}
      className={clsxs(rootStyle, delegated.className)}
      format={format}
      locale={delegated.locale ?? locale}
      max={max}
    >
      {label && <BaseProgress.Label className={labelStyle}>{label}</BaseProgress.Label>}

      <div className={clsx(wrapperStyle, type === 'percent' ? wrapperDirectionStyles.inline : wrapperDirectionStyles.block)}>
        <BaseProgress.Track className={clsx(trackStyle)}>
          <BaseProgress.Indicator className={indicatorStyle} />
        </BaseProgress.Track>

        {delegated.value !== null && (
          <div className={valueStyle}>
            <BaseProgress.Value />

            {formattedMax !== null && ` / ${formattedMax}`}
          </div>
        )}
      </div>
    </BaseProgress.Root>
  );
};
